import { SiteChartsConfig } from '../../components/SiteCharts';

/**
 * Chart configurations for ExternalPerformance (global/region-level charts).
 * These configs are used with the GlobalCharts template (no device dependency).
 * Mirrors Site E LTE chart groups: Accessibility, Retainability, Handover, Data Volume, ENDC.
 */

// ============================================================
// Accessibility
// ============================================================

export const globalErabChartConfig: SiteChartsConfig = {
  title: 'ERAB Establishment',
  apiEndpoint: '/api/lte/global-erab-establishment',
  csvFilename: 'global_erab_establishment',
  chartImageFilename: 'global_erab_establishment_chart',
  series: [
    { name: 'ERAB Establishment Success', type: 'bar', yAxisIndex: 0, color: 'success', dataKey: 'erab_success' },
    { name: 'ERAB Establishment Attempt', type: 'bar', yAxisIndex: 0, color: 'warning', dataKey: 'erab_attempt' },
    { name: 'ERAB Establishment SR', type: 'line', yAxisIndex: 1, color: 'total', dataKey: 'erab_establishment_sr', isPercentage: true },
  ],
  yAxes: [
    { name: 'Count', position: 'right', nameGap: 50, formatter: '{value}' },
    { name: 'Data Accessibility %', position: 'left', min: 0, max: 100, nameGap: 50, formatter: '{value}%' },
  ],
  tableColumns: [
    { header: 'ERAB Success', dataKey: 'erab_success' },
    { header: 'ERAB Attempt', dataKey: 'erab_attempt' },
    { header: 'ERAB SR (%)', dataKey: 'erab_establishment_sr', isPercentage: true },
  ],
};

export const globalVolteChartConfig: SiteChartsConfig = {
  title: 'VoLTE Establishment',
  apiEndpoint: '/api/lte/global-volte-establishment',
  csvFilename: 'global_volte_establishment',
  chartImageFilename: 'global_volte_establishment_chart',
  series: [
    { name: 'VoLTE Establishment Success', type: 'bar', yAxisIndex: 0, color: 'success', dataKey: 'volte_success' },
    { name: 'VoLTE Establishment Attempt', type: 'bar', yAxisIndex: 0, color: 'warning', dataKey: 'volte_attempt' },
    { name: 'VoLTE Establishment SR', type: 'line', yAxisIndex: 1, color: 'total', dataKey: 'volte_establishment_sr', isPercentage: true },
  ],
  yAxes: [
    { name: 'Count', position: 'right', nameGap: 50, formatter: '{value}' },
    { name: 'Data Accessibility %', position: 'left', min: 0, max: 100, nameGap: 50, formatter: '{value}%' },
  ],
  tableColumns: [
    { header: 'VoLTE Success', dataKey: 'volte_success' },
    { header: 'VoLTE Attempt', dataKey: 'volte_attempt' },
    { header: 'VoLTE SR (%)', dataKey: 'volte_establishment_sr', isPercentage: true },
  ],
};

export const globalRrcConnectionChartConfig: SiteChartsConfig = {
  title: 'RRC Connection Establishment',
  apiEndpoint: '/api/lte/global-rrc-connection-establishment',
  csvFilename: 'global_rrc_connection_establishment',
  chartImageFilename: 'global_rrc_connection_establishment_chart',
  series: [
    { name: 'RRC Connection Establishment Success', type: 'bar', yAxisIndex: 0, color: 'success', dataKey: 'rrc_success' },
    { name: 'RRC Connection Establishment Attempt', type: 'bar', yAxisIndex: 0, color: 'warning', dataKey: 'rrc_attempt' },
    { name: 'RRC Connection Establishment SR', type: 'line', yAxisIndex: 1, color: 'total', dataKey: 'rrc_establishment_sr', isPercentage: true },
  ],
  yAxes: [
    { name: 'Count', position: 'right', nameGap: 50, formatter: '{value}' },
    { name: 'Success Rate %', position: 'left', min: 0, max: 100, nameGap: 50, formatter: '{value}%' },
  ],
  tableColumns: [
    { header: 'RRC Success', dataKey: 'rrc_success' },
    { header: 'RRC Attempt', dataKey: 'rrc_attempt' },
    { header: 'RRC SR (%)', dataKey: 'rrc_establishment_sr', isPercentage: true },
  ],
};

// ============================================================
// Retainability
// ============================================================

export const globalErabDropRateChartConfig: SiteChartsConfig = {
  title: 'ERAB Drop Rate',
  apiEndpoint: '/api/lte/global-erab-drop-rate',
  csvFilename: 'global_erab_drop_rate',
  chartImageFilename: 'global_erab_drop_rate_chart',
  series: [
    { name: 'ERAB Drop Counts', type: 'bar', yAxisIndex: 0, color: 'warning', dataKey: 'erab_drop_counts' },
    { name: 'ERAB Drop Rate', type: 'line', yAxisIndex: 1, color: 'total', dataKey: 'erab_drop_rate', isPercentage: true },
  ],
  yAxes: [
    { name: 'Count', position: 'right', nameGap: 50, formatter: '{value}' },
    { name: 'Drop Rate %', position: 'left', nameGap: 50, formatter: '{value}%' },
  ],
  tableColumns: [
    { header: 'ERAB Drop Counts', dataKey: 'erab_drop_counts' },
    { header: 'ERAB Drop Rate (%)', dataKey: 'erab_drop_rate', isPercentage: true },
  ],
};

export const globalVolteDropRateChartConfig: SiteChartsConfig = {
  title: 'VoLTE Drop Rate',
  apiEndpoint: '/api/lte/global-volte-drop-rate',
  csvFilename: 'global_volte_drop_rate',
  chartImageFilename: 'global_volte_drop_rate_chart',
  series: [
    { name: 'VoLTE Drop Counts', type: 'bar', yAxisIndex: 0, color: 'warning', dataKey: 'volte_drop_counts' },
    { name: 'VoLTE Drop Rate', type: 'line', yAxisIndex: 1, color: 'total', dataKey: 'volte_drop_rate', isPercentage: true },
  ],
  yAxes: [
    { name: 'Count', position: 'right', nameGap: 50, formatter: '{value}' },
    { name: 'Drop Rate %', position: 'left', nameGap: 50, formatter: '{value}%' },
  ],
  tableColumns: [
    { header: 'VoLTE Drop Counts', dataKey: 'volte_drop_counts' },
    { header: 'VoLTE Drop Rate (%)', dataKey: 'volte_drop_rate', isPercentage: true },
  ],
};

// ============================================================
// Handover
// ============================================================

export const globalHandinOverS1ChartConfig: SiteChartsConfig = {
  title: 'Handin Over S1 Interface',
  apiEndpoint: '/api/lte/global-handin-over-s1',
  csvFilename: 'global_handin_over_s1',
  chartImageFilename: 'global_handin_over_s1_chart',
  series: [
    { name: 'HandIn Success', type: 'bar', yAxisIndex: 0, color: 'success', dataKey: 'handin_success' },
    { name: 'HandIn Attempts', type: 'bar', yAxisIndex: 0, color: 'warning', dataKey: 'handin_attempts' },
    { name: 'HandIn Success Rate', type: 'line', yAxisIndex: 1, color: 'total', dataKey: 'handin_success_rate', isPercentage: true },
  ],
  yAxes: [
    { name: 'Count', position: 'right', nameGap: 50, formatter: '{value}' },
    { name: 'Success Rate %', position: 'left', min: 0, max: 100, nameGap: 50, formatter: '{value}%' },
  ],
  tableColumns: [
    { header: 'HandIn Success', dataKey: 'handin_success' },
    { header: 'HandIn Attempts', dataKey: 'handin_attempts' },
    { header: 'HandIn Success Rate (%)', dataKey: 'handin_success_rate', isPercentage: true },
  ],
};

export const globalHandoutOverS1ChartConfig: SiteChartsConfig = {
  title: 'Handout Over S1 Interface',
  apiEndpoint: '/api/lte/global-handout-over-s1',
  csvFilename: 'global_handout_over_s1',
  chartImageFilename: 'global_handout_over_s1_chart',
  series: [
    { name: 'HandOut Success', type: 'bar', yAxisIndex: 0, color: 'success', dataKey: 'handout_success' },
    { name: 'HandOut Attempts', type: 'bar', yAxisIndex: 0, color: 'warning', dataKey: 'handout_attempts' },
    { name: 'HandOut Success Rate', type: 'line', yAxisIndex: 1, color: 'total', dataKey: 'handout_success_rate', isPercentage: true },
  ],
  yAxes: [
    { name: 'Count', position: 'right', nameGap: 50, formatter: '{value}' },
    { name: 'Success Rate %', position: 'left', min: 0, max: 100, nameGap: 50, formatter: '{value}%' },
  ],
  tableColumns: [
    { header: 'HandOut Success', dataKey: 'handout_success' },
    { header: 'HandOut Attempts', dataKey: 'handout_attempts' },
    { header: 'HandOut Success Rate (%)', dataKey: 'handout_success_rate', isPercentage: true },
  ],
};

export const globalIntraFreqX1HoChartConfig: SiteChartsConfig = {
  title: 'Intra-frequency X1-HO',
  apiEndpoint: '/api/lte/global-intra-freq-x1-ho',
  csvFilename: 'global_intra_freq_x1_ho',
  chartImageFilename: 'global_intra_freq_x1_ho_chart',
  series: [
    { name: 'Handover Success', type: 'bar', yAxisIndex: 0, color: 'success', dataKey: 'handover_success' },
    { name: 'Handover Attempts', type: 'bar', yAxisIndex: 0, color: 'warning', dataKey: 'handover_attempts' },
    { name: 'Handover Success Rate', type: 'line', yAxisIndex: 1, color: 'total', dataKey: 'handover_success_rate', isPercentage: true },
  ],
  yAxes: [
    { name: 'Count', position: 'right', nameGap: 50, formatter: '{value}' },
    { name: 'Success Rate %', position: 'left', min: 0, max: 100, nameGap: 50, formatter: '{value}%' },
  ],
  tableColumns: [
    { header: 'Handover Success', dataKey: 'handover_success' },
    { header: 'Handover Attempts', dataKey: 'handover_attempts' },
    { header: 'Handover Success Rate (%)', dataKey: 'handover_success_rate', isPercentage: true },
  ],
};

export const globalInterFreqX1HoChartConfig: SiteChartsConfig = {
  title: 'Inter-frequency X1-HO',
  apiEndpoint: '/api/lte/global-inter-freq-x1-ho',
  csvFilename: 'global_inter_freq_x1_ho',
  chartImageFilename: 'global_inter_freq_x1_ho_chart',
  series: [
    { name: 'Handover Success', type: 'bar', yAxisIndex: 0, color: 'success', dataKey: 'handover_success' },
    { name: 'Handover Attempts', type: 'bar', yAxisIndex: 0, color: 'warning', dataKey: 'handover_attempts' },
    { name: 'Handover Success Rate', type: 'line', yAxisIndex: 1, color: 'total', dataKey: 'handover_success_rate', isPercentage: true },
  ],
  yAxes: [
    { name: 'Count', position: 'right', nameGap: 50, formatter: '{value}' },
    { name: 'Success Rate %', position: 'left', min: 0, max: 100, nameGap: 50, formatter: '{value}%' },
  ],
  tableColumns: [
    { header: 'Handover Success', dataKey: 'handover_success' },
    { header: 'Handover Attempts', dataKey: 'handover_attempts' },
    { header: 'Handover Success Rate (%)', dataKey: 'handover_success_rate', isPercentage: true },
  ],
};

// ============================================================
// Data Volume
// ============================================================

export const globalDlGtpDataVolumeChartConfig: SiteChartsConfig = {
  title: 'DL GTP Data Volume',
  apiEndpoint: '/api/lte/global-dl-gtp-data-volume',
  csvFilename: 'global_dl_gtp_data_volume',
  chartImageFilename: 'global_dl_gtp_data_volume_chart',
  series: [
    { name: 'DL Data Volume', type: 'bar', yAxisIndex: 0, color: 'warning', dataKey: 'dl_gtp_data_volume' },
  ],
  yAxes: [
    { name: 'Volume (GB)', position: 'left', nameGap: 70, formatter: '{value}' },
    { name: '', position: 'right', formatter: '' },
  ],
  tableColumns: [
    { header: 'DL Data Volume (GB)', dataKey: 'dl_gtp_data_volume' },
  ],
};

export const globalUlGtpDataVolumeChartConfig: SiteChartsConfig = {
  title: 'UL GTP Data Volume',
  apiEndpoint: '/api/lte/global-ul-gtp-data-volume',
  csvFilename: 'global_ul_gtp_data_volume',
  chartImageFilename: 'global_ul_gtp_data_volume_chart',
  series: [
    { name: 'UL Data Volume', type: 'bar', yAxisIndex: 0, color: 'warning', dataKey: 'ul_gtp_data_volume' },
  ],
  yAxes: [
    { name: 'Volume (GB)', position: 'left', nameGap: 70, formatter: '{value}' },
    { name: '', position: 'right', formatter: '' },
  ],
  tableColumns: [
    { header: 'UL Data Volume (GB)', dataKey: 'ul_gtp_data_volume' },
  ],
};

// ============================================================
// ENDC
// ============================================================

export const globalMaximumEndcChartConfig: SiteChartsConfig = {
  title: 'Maximum ENDC',
  apiEndpoint: '/api/lte/global-maximum-endc',
  csvFilename: 'global_maximum_endc',
  chartImageFilename: 'global_maximum_endc_chart',
  series: [
    { name: 'Maximum ENDC Connected UE Sessions', type: 'bar', yAxisIndex: 0, color: 'warning', dataKey: 'maximum_endc_connected_ue_sessions' },
  ],
  yAxes: [
    { name: 'Sessions', position: 'left', nameGap: 70, formatter: '{value}' },
    { name: '', position: 'right', formatter: '' },
  ],
  tableColumns: [
    { header: 'Maximum ENDC Connected UE Sessions', dataKey: 'maximum_endc_connected_ue_sessions' },
  ],
};

export const globalSgNbAdditionChartConfig: SiteChartsConfig = {
  title: 'SgNB Addition',
  apiEndpoint: '/api/lte/global-sgnb-addition',
  csvFilename: 'global_sgnb_addition',
  chartImageFilename: 'global_sgnb_addition_chart',
  series: [
    { name: 'SgNB Addition ACK Success', type: 'bar', yAxisIndex: 0, color: 'success', dataKey: 'sgnb_addition_ack_success' },
    { name: 'SgNB Addition Attempts', type: 'bar', yAxisIndex: 0, color: 'warning', dataKey: 'sgnb_addition_attempts' },
    { name: 'SgNB Addition SR', type: 'line', yAxisIndex: 1, color: 'total', dataKey: 'sgnb_addition_sr', isPercentage: true },
  ],
  yAxes: [
    { name: 'Count', position: 'right', nameGap: 50, formatter: '{value}' },
    { name: 'Success Rate %', position: 'left', min: 0, max: 100, nameGap: 50, formatter: '{value}%' },
  ],
  tableColumns: [
    { header: 'SgNB Addition ACK Success', dataKey: 'sgnb_addition_ack_success' },
    { header: 'SgNB Addition Attempts', dataKey: 'sgnb_addition_attempts' },
    { header: 'SgNB Addition SR (%)', dataKey: 'sgnb_addition_sr', isPercentage: true },
  ],
};

/**
 * Global LTE chart group definitions — mirrors Site E tab layout.
 * Used by ExternalPerformance to render grouped charts.
 */
export interface GlobalChartGroupConfig {
  groupTitle: string;
  configs: SiteChartsConfig[];
}

export const GLOBAL_LTE_CHART_GROUPS: GlobalChartGroupConfig[] = [
  {
    groupTitle: 'Accessibility',
    configs: [globalErabChartConfig, globalVolteChartConfig, globalRrcConnectionChartConfig],
  },
  {
    groupTitle: 'Retainability',
    configs: [globalErabDropRateChartConfig, globalVolteDropRateChartConfig],
  },
  {
    groupTitle: 'Handover',
    configs: [globalHandinOverS1ChartConfig, globalHandoutOverS1ChartConfig, globalIntraFreqX1HoChartConfig, globalInterFreqX1HoChartConfig],
  },
  {
    groupTitle: 'Data Volume',
    configs: [globalDlGtpDataVolumeChartConfig, globalUlGtpDataVolumeChartConfig],
  },
  {
    groupTitle: 'ENDC',
    configs: [globalMaximumEndcChartConfig, globalSgNbAdditionChartConfig],
  },
];

/**
 * Maps each chart's csvFilename (used as component key) to the batch response key.
 * The batch endpoint /api/lte/global-all-charts returns an object keyed by these values.
 */
export const BATCH_DATA_KEY_MAP: Record<string, string> = {
  global_erab_establishment: 'erab_establishment',
  global_volte_establishment: 'volte_establishment',
  global_rrc_connection_establishment: 'rrc_connection_establishment',
  global_erab_drop_rate: 'erab_drop_rate',
  global_volte_drop_rate: 'volte_drop_rate',
  global_handin_over_s1: 'handin_over_s1',
  global_handout_over_s1: 'handout_over_s1',
  global_intra_freq_x1_ho: 'intra_freq_x1_ho',
  global_inter_freq_x1_ho: 'inter_freq_x1_ho',
  global_dl_gtp_data_volume: 'dl_gtp_data_volume',
  global_ul_gtp_data_volume: 'ul_gtp_data_volume',
  global_maximum_endc: 'maximum_endc',
  global_sgnb_addition: 'sgnb_addition',
};

// ============================================================
// NR Global Chart Configurations
// ============================================================

export const globalSgNbAdditionNrChartConfig: SiteChartsConfig = {
  title: 'SgNB Addition',
  apiEndpoint: '/api/nr/global-sgnb-addition',
  csvFilename: 'global_sgnb_addition_nr',
  chartImageFilename: 'global_sgnb_addition_nr_chart',
  series: [
    { name: 'SgNB Addition Attempts', type: 'bar', yAxisIndex: 0, color: 'warning', dataKey: 'sgnb_addition_attempts' },
    { name: 'SgNB Addition ACK Success', type: 'bar', yAxisIndex: 0, color: 'success', dataKey: 'sgnb_addition_ack_success' },
    { name: 'SgNB Addition SR', type: 'line', yAxisIndex: 1, color: 'total', dataKey: 'sgnb_addition_sr', isPercentage: true },
  ],
  yAxes: [
    { name: 'Count', position: 'right', nameGap: 50, formatter: '{value}' },
    { name: 'Success Rate %', position: 'left', min: 0, max: 100, nameGap: 50, formatter: '{value}%' },
  ],
  tableColumns: [
    { header: 'SgNB Addition Attempts', dataKey: 'sgnb_addition_attempts' },
    { header: 'SgNB Addition ACK Success', dataKey: 'sgnb_addition_ack_success' },
    { header: 'SgNB Addition SR (%)', dataKey: 'sgnb_addition_sr', isPercentage: true },
  ],
};

export const globalMaximumEndcNrChartConfig: SiteChartsConfig = {
  title: 'Maximum ENDC',
  apiEndpoint: '/api/nr/global-maximum-endc',
  csvFilename: 'global_maximum_endc_nr',
  chartImageFilename: 'global_maximum_endc_nr_chart',
  series: [
    { name: 'Maximum ENDC Sessions', type: 'bar', yAxisIndex: 0, color: 'warning', dataKey: 'maximum_endc_sessions' },
  ],
  yAxes: [
    { name: 'Sessions', position: 'left', nameGap: 70, formatter: '{value}' },
    { name: '', position: 'right', formatter: '' },
  ],
  tableColumns: [
    { header: 'Maximum ENDC Sessions', dataKey: 'maximum_endc_sessions' },
  ],
};

export const globalEndcUeSessionDropNrChartConfig: SiteChartsConfig = {
  title: 'ENDC UE Session Drop Rate',
  apiEndpoint: '/api/nr/global-endc-ue-session-drop',
  csvFilename: 'global_endc_ue_session_drop_nr',
  chartImageFilename: 'global_endc_ue_session_drop_nr_chart',
  series: [
    { name: 'ENDC UE Session Drop Count', type: 'bar', yAxisIndex: 0, color: 'warning', dataKey: 'endc_ue_session_drop_count' },
    { name: 'ENDC UE Session Drop Rate', type: 'line', yAxisIndex: 1, color: 'total', dataKey: 'endc_ue_session_drop_rate', isPercentage: true },
  ],
  yAxes: [
    { name: 'Count', position: 'right', nameGap: 50, formatter: '{value}' },
    { name: 'Drop Rate %', position: 'left', nameGap: 50, formatter: '{value}%' },
  ],
  tableColumns: [
    { header: 'ENDC UE Session Drop Count', dataKey: 'endc_ue_session_drop_count' },
    { header: 'ENDC UE Session Drop Rate (%)', dataKey: 'endc_ue_session_drop_rate', isPercentage: true },
  ],
};

export const globalDlGtpDataVolumeNrChartConfig: SiteChartsConfig = {
  title: 'DL GTP Data Volume',
  apiEndpoint: '/api/nr/global-dl-gtp-data-volume',
  csvFilename: 'global_dl_gtp_data_volume_nr',
  chartImageFilename: 'global_dl_gtp_data_volume_nr_chart',
  series: [
    { name: 'DL Data Volume', type: 'bar', yAxisIndex: 0, color: 'warning', dataKey: 'dl_s1u_data_volume' },
  ],
  yAxes: [
    { name: 'Volume (GB)', position: 'left', nameGap: 70, formatter: '{value}' },
    { name: '', position: 'right', formatter: '' },
  ],
  tableColumns: [
    { header: 'DL Data Volume (GB)', dataKey: 'dl_s1u_data_volume' },
  ],
};

export const globalUlGtpDataVolumeNrChartConfig: SiteChartsConfig = {
  title: 'UL GTP Data Volume',
  apiEndpoint: '/api/nr/global-ul-gtp-data-volume',
  csvFilename: 'global_ul_gtp_data_volume_nr',
  chartImageFilename: 'global_ul_gtp_data_volume_nr_chart',
  series: [
    { name: 'UL Data Volume', type: 'bar', yAxisIndex: 0, color: 'warning', dataKey: 'ul_s1u_data_volume' },
  ],
  yAxes: [
    { name: 'Volume (GB)', position: 'left', nameGap: 70, formatter: '{value}' },
    { name: '', position: 'right', formatter: '' },
  ],
  tableColumns: [
    { header: 'UL Data Volume (GB)', dataKey: 'ul_s1u_data_volume' },
  ],
};

export const globalIntraCuHandoverNrChartConfig: SiteChartsConfig = {
  title: 'Intra CU Handover Success',
  apiEndpoint: '/api/nr/global-intra-cu-handover',
  csvFilename: 'global_intra_cu_handover_nr',
  chartImageFilename: 'global_intra_cu_handover_nr_chart',
  series: [
    { name: 'Intra CU Handover Attempts', type: 'bar', yAxisIndex: 1, color: 'warning', dataKey: 'intra_cu_handover_attempts' },
    { name: 'Intra CU Handover Successes', type: 'bar', yAxisIndex: 1, color: 'success', dataKey: 'intra_cu_handover_successes' },
    { name: 'Success Rate', type: 'line', yAxisIndex: 0, color: 'total', dataKey: 'intra_cu_handover_sr', isPercentage: true },
  ],
  yAxes: [
    { name: 'Success Rate (%)', position: 'left', nameGap: 70, formatter: '{value}%' },
    { name: 'Count', position: 'right', nameGap: 50, formatter: '{value}' },
  ],
  tableColumns: [
    { header: 'Intra CU Handover Attempts', dataKey: 'intra_cu_handover_attempts' },
    { header: 'Intra CU Handover Successes', dataKey: 'intra_cu_handover_successes' },
    { header: 'Success Rate (%)', dataKey: 'intra_cu_handover_sr', isPercentage: true },
  ],
};

export const globalEndcDataAccessibilityNrChartConfig: SiteChartsConfig = {
  title: 'ENDC Data Accessibility',
  apiEndpoint: '/api/nr/global-endc-data-accessibility',
  csvFilename: 'global_endc_data_accessibility_nr',
  chartImageFilename: 'global_endc_data_accessibility_nr_chart',
  series: [
    { name: 'ENDC_ERAB_Attempts', type: 'bar', yAxisIndex: 1, color: 'warning', dataKey: 'endc_erab_attempts' },
    { name: 'ENDC Data Accessibility (%)', type: 'line', yAxisIndex: 0, color: 'total', dataKey: 'endc_data_accessibility', isPercentage: true },
  ],
  yAxes: [
    { name: 'Data Accessibility %', position: 'left', min: 0, max: 100, nameGap: 50, formatter: '{value}%' },
    { name: 'Count', position: 'right', nameGap: 50, formatter: '{value}' },
  ],
  tableColumns: [
    { header: 'ENDC ERAB Attempts', dataKey: 'endc_erab_attempts' },
    { header: 'ENDC Data Accessibility (%)', dataKey: 'endc_data_accessibility', isPercentage: true },
  ],
};

export const GLOBAL_NR_CHART_GROUPS: GlobalChartGroupConfig[] = [
  {
    groupTitle: 'ENDC',
    configs: [globalSgNbAdditionNrChartConfig, globalMaximumEndcNrChartConfig, globalEndcUeSessionDropNrChartConfig, globalEndcDataAccessibilityNrChartConfig],
  },
  {
    groupTitle: 'Data Volume',
    configs: [globalDlGtpDataVolumeNrChartConfig, globalUlGtpDataVolumeNrChartConfig],
  },
  {
    groupTitle: 'Handover',
    configs: [globalIntraCuHandoverNrChartConfig],
  },
];

export const NR_BATCH_DATA_KEY_MAP: Record<string, string> = {
  global_sgnb_addition_nr: 'sgnb_addition',
  global_maximum_endc_nr: 'maximum_endc',
  global_endc_ue_session_drop_nr: 'endc_ue_session_drop',
  global_dl_gtp_data_volume_nr: 'dl_gtp_data_volume',
  global_ul_gtp_data_volume_nr: 'ul_gtp_data_volume',
  global_intra_cu_handover_nr: 'intra_cu_handover',
  global_endc_data_accessibility_nr: 'endc_data_accessibility',
};
